import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "../../api";
import { APageHead, ATable, ABadge } from "../adminKit";

const CATS = ["Article", "Guide", "Checklist", "FAQ", "Glossary term", "Business-credit lesson", "Personal-credit lesson", "Net-30 education", "Platform announcement"];
const ACCESS = ["public", "free", "pro", "business", "enterprise"];

export default function AdminContent() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", summary: "", body: "", category: "Article", status: "draft", access_level: "public" });
  const load = () => api.get("/admin/content").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try { await api.post("/admin/content", form); setOpen(false); setForm({ title: "", summary: "", body: "", category: "Article", status: "draft", access_level: "public" }); toast.success("Content created"); load(); }
    catch { toast.error("Failed"); }
  };
  const setStatus = async (id, status) => { try { await api.put(`/admin/content/${id}`, { title: rows.find((r) => r.id === id).title, status }); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-4">
      <APageHead title="Content Management" subtitle="Lightweight CMS for the Learning Center. Submitted content is sanitized."
        action={<button onClick={() => setOpen(true)} className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-500" data-testid="content-new"><Plus size={15} /> New Content</button>} />
      <ATable loading={false} rows={rows} columns={[
        { key: "title", label: "Title", render: (r) => <span className="text-white">{r.title}</span> },
        { key: "category", label: "Category", render: (r) => <ABadge tone="blue">{r.category}</ABadge> },
        { key: "access_level", label: "Access", render: (r) => <ABadge tone="purple">{r.access_level}</ABadge> },
        { key: "status", label: "Status", render: (r) => (
          <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="cc-input rounded-lg px-2 py-1 text-xs">
            {["draft", "scheduled", "published", "archived"].map((s) => <option key={s} className="bg-[#1a1a24]">{s}</option>)}
          </select>
        )},
        { key: "author", label: "Author" },
      ]} empty="No content yet." />
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative cc-card rounded-2xl p-6 w-full max-w-lg space-y-3">
            <h3 className="cc-heading text-lg font-500 text-white">New Content</h3>
            <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" data-testid="content-title" />
            <input placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" />
            <textarea placeholder="Body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="cc-input rounded-xl px-2 py-2.5 text-sm">{CATS.map((c) => <option key={c} className="bg-[#1a1a24]">{c}</option>)}</select>
              <select value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })} className="cc-input rounded-xl px-2 py-2.5 text-sm">{ACCESS.map((c) => <option key={c} className="bg-[#1a1a24]">{c}</option>)}</select>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="cc-input rounded-xl px-2 py-2.5 text-sm">{["draft", "published"].map((c) => <option key={c} className="bg-[#1a1a24]">{c}</option>)}</select>
            </div>
            <button onClick={create} className="cc-btn-primary w-full rounded-xl py-2.5 text-sm font-500" data-testid="content-create">Create</button>
          </div>
        </div>
      )}
    </div>
  );
}
